import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, AlertCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import SeoHead from "../components/SeoHead";
import MedicationInteractionsPage from "./MedicationInteractionsPage";
import IngredientPage from "./IngredientPage";

const VERDICT_VISUAL = {
  HIGH: {
    chip: "bg-rose-100 text-rose-800 border-rose-200",
    Icon: AlertCircle,
    label: "Avoid",
  },
  MODERATE: {
    chip: "bg-amber-100 text-amber-900 border-amber-200",
    Icon: AlertTriangle,
    label: "Caution",
  },
  LOW: {
    chip: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Icon: CheckCircle2,
    label: "No label conflict found",
  },
};

function Skeleton() {
  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="ingredient-pair-skeleton">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 space-y-6 animate-pulse">
        <div className="h-3 w-1/4 bg-cream-200 rounded" />
        <div className="h-12 w-3/4 bg-cream-200 rounded" />
        <div className="h-8 w-32 bg-cream-200 rounded-full" />
        <div className="h-32 w-full bg-cream-200 rounded-2xl" />
        <div className="h-24 w-full bg-cream-200 rounded-2xl" />
      </div>
    </main>
  );
}

function NotFound({ pair }) {
  return (
    <main className="bg-cream-50 min-h-[80vh] flex items-center justify-center px-5">
      <SeoHead
        title={`${pair} — Blendsafe`}
        description="We don't have data on this ingredient pair yet. Check your full routine."
        canonicalPath={`/ingredients/${pair}`}
      />
      <div
        data-testid="ingredient-pair-not-found"
        className="max-w-md text-center space-y-5"
      >
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink">
          We don't have data on this combination yet.
        </h1>
        <p className="text-ink-soft">
          Run a personalized check to see how these ingredients interact with
          your medications and the rest of your routine.
        </p>
        <Link
          to="/check"
          data-testid="ingredient-pair-cta-not-found"
          className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Check your routine <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </main>
  );
}

function PairView({ data }) {
  const visual = VERDICT_VISUAL[data.verdict] || VERDICT_VISUAL.MODERATE;
  const Icon = visual.Icon;
  const a = data.ingredient_a?.name;
  const b = data.ingredient_b?.name;
  const aSlug = data.ingredient_a?.slug;
  const bSlug = data.ingredient_b?.slug;
  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="ingredient-pair-page">
      <SeoHead
        title={data.title}
        description={data.meta_description}
        canonicalPath={`/ingredients/${aSlug}-and-${bSlug}`}
      />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 space-y-8">
        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-ink-mute">
          Ingredient compatibility
        </p>
        <h1 className="text-3xl sm:text-5xl font-extrabold tracking-[-0.02em] text-ink leading-[1.05]">
          {data.h1}
        </h1>
        {data.hand_written?.active && (
          <div data-testid="pair-handwritten" className="space-y-3">
            <p className="text-[13px] text-ink-mute">
              Written by{" "}
              <Link to="/about" className="font-semibold text-ink underline underline-offset-2">
                {data.hand_written.author_name}
              </Link>
              , PharmD student
              {data.hand_written.last_updated ? ` · Reviewed ${data.hand_written.last_updated}` : ""}
            </p>
            <div className="blog-prose">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{ a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" /> }}
              >
                {data.hand_written.content}
              </ReactMarkdown>
            </div>
            <hr className="border-surface-line" />
          </div>
        )}
        <span
          data-testid="ingredient-pair-verdict"
          className={`inline-flex items-center gap-2 px-4 h-9 rounded-full border font-bold text-sm ${visual.chip}`}
        >
          <Icon className="w-4 h-4" /> {data.verdict_label || visual.label}
        </span>
        <p className="text-[11.5px] leading-relaxed text-ink-mute">
          Information from the FDA label and published sources. Not medical advice.
        </p>

        {Array.isArray(data.clinical_warnings) && data.clinical_warnings.length > 0 && (
          <div
            data-testid="ingredient-pair-clinical-warning"
            className="rounded-2xl border border-rose-200 bg-rose-50 p-5 sm:p-6 space-y-3"
          >
            <div className="flex items-center gap-2 text-rose-800 font-bold text-sm">
              <AlertCircle className="w-4 h-4" /> Prescription-strength warning
            </div>
            {data.clinical_warnings.map((w, i) => (
              <p
                key={i}
                data-testid={`clinical-warning-${i}`}
                className="text-rose-900/90 leading-relaxed text-[14px]"
              >
                <span className="font-semibold">{w.term}:</span> {w.note}
              </p>
            ))}
          </div>
        )}

        <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-3">
          <h2 className="text-lg font-bold text-ink">Why</h2>
          <p className="text-ink-soft leading-relaxed text-[15px]">{data.why}</p>
        </section>

        <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-3">
          <h2 className="text-lg font-bold text-ink">Usage guidance</h2>
          <p className="text-ink-soft leading-relaxed text-[15px]">{data.guidance}</p>
        </section>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Link
            to="/routine"
            data-testid="ingredient-pair-cta"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Check your full routine <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/check"
            className="text-sm font-semibold text-ink-soft underline decoration-from-font underline-offset-4 hover:text-ink"
          >
            or run a single check
          </Link>
        </div>

        <p className="text-[11px] text-ink-mute pt-4 border-t border-surface-line">
          Educational content — not medical advice. Always check with your
          dermatologist for ingredient-specific questions.
        </p>
      </div>
    </main>
  );
}

export default function IngredientPairPage({ onAuthNeeded }) {
  const { combined } = useParams();
  const isPair = !!(combined && combined.includes("-and-"));

  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  // Single-segment dispatcher: probe the ingredient endpoint. If it's a known
  // canonical ingredient → render the new IngredientPage. If 404 → fall back
  // to MedicationInteractionsPage (preserves legacy /ingredients/{med} links).
  const [singleData, setSingleData] = useState(null);
  const [singleStatus, setSingleStatus] = useState("loading");

  useEffect(() => {
    if (!isPair) return;
    let cancelled = false;
    setStatus("loading");
    api
      .get(`/seo/ingredients/${encodeURIComponent(combined)}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setStatus("ok");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("notfound");
      });
    return () => {
      cancelled = true;
    };
  }, [combined, isPair]);

  useEffect(() => {
    if (isPair || !combined) return;
    let cancelled = false;
    setSingleStatus("loading");
    api
      .get(`/seo/ingredient/${encodeURIComponent(combined)}`)
      .then((r) => {
        if (cancelled) return;
        setSingleData(r.data);
        setSingleStatus("ingredient");
      })
      .catch(() => {
        if (cancelled) return;
        setSingleStatus("medication");
      });
    return () => {
      cancelled = true;
    };
  }, [combined, isPair]);

  // Single-segment URL: dispatch between IngredientPage / MedicationInteractionsPage.
  if (!isPair) {
    if (singleStatus === "loading") return <Skeleton />;
    if (singleStatus === "ingredient")
      return <IngredientPage initialData={singleData} onAuthNeeded={onAuthNeeded} />;
    return <MedicationInteractionsPage />;
  }

  if (status === "loading") return <Skeleton />;
  if (status === "notfound") return <NotFound pair={combined} />;
  return <PairView data={data} />;
}
